import * as React from 'react';
import { clsx } from 'clsx';

export interface TextAreaProps {
  /**
   * The label for the textarea
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current value
   */
  value?: string;
  /**
   * Change handler
   */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * Validation state - affects border color
   */
  state?: 'default' | 'success' | 'error';
  /**
   * Error message (implies error state)
   */
  error?: string;
  /**
   * Helper text shown below input
   */
  helperText?: string;
  /**
   * Whether the textarea is disabled
   */
  disabled?: boolean;
  /**
   * Number of rows (affects height)
   */
  rows?: number;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
}

const labelStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#2f2f2f',
  marginBottom: '0px',
  display: 'block',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const textareaWrapperStyles: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

const textareaStyles: React.CSSProperties = {
  width: '100%',
  minHeight: '72px',
  padding: '8px',
  fontSize: '13px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  border: '1px solid',
  borderRadius: '8px',
  outline: 'none',
  resize: 'vertical',
  transition: 'all 0.2s ease-in-out',
  lineHeight: '1.5',
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '13px',
  marginTop: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

/**
 * TextArea component - Arbor Design System
 *
 * A multi-line text input with label, helper text, and validation states.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      state = 'default',
      error,
      helperText,
      disabled = false,
      rows = 3,
      className,
      style,
      'data-testid': dataTestId,
      name,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const textareaId = React.useId();

    // Determine the actual state (error overrides state prop)
    const actualState = error ? 'error' : state;

    // Get border color based on state and interactions
    const getBorderColor = (): string => {
      if (disabled) {
        return '#dfdfdf';
      }

      if (actualState === 'error') {
        return '#c93232';
      }

      if (actualState === 'success') {
        return '#0e8a0e';
      }

      if (isFocused) {
        return '#0e8a0e';
      }

      if (isHovered) {
        return '#7e7e7e';
      }

      return '#dfdfdf';
    };

    // Get background color
    const getBackgroundColor = (): string => {
      if (disabled) {
        return '#efefef';
      }
      return '#ffffff';
    };

    // Get box shadow (focus ring)
    const getBoxShadow = (): string | undefined => {
      if (isFocused && !disabled) {
        if (actualState === 'error') {
          return '0px 0px 0px 3px rgba(201, 50, 50, 0.2)';
        }
        return '0px 0px 0px 3px #3cad51';
      }
      return undefined;
    };

    const textareaStylesCombined: React.CSSProperties = {
      ...textareaStyles,
      borderColor: getBorderColor(),
      backgroundColor: getBackgroundColor(),
      boxShadow: getBoxShadow(),
      color: disabled ? '#7e7e7e' : '#2f2f2f',
      cursor: disabled ? 'not-allowed' : 'text',
    };

    const helperColor = error ? '#c93232' : actualState === 'success' ? '#0e8a0e' : '#595959';

    return (
      <div
        className={clsx('arbor-textarea-wrapper', className)}
        style={style}
        data-testid={dataTestId}
      >
        {label && (
          <label htmlFor={textareaId} style={labelStyles}>
            {label}
          </label>
        )}
        {label && <div style={{ height: '8px' }} />}
        <div style={textareaWrapperStyles}>
          <textarea
            ref={ref}
            id={textareaId}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            rows={rows}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={textareaStylesCombined}
          />
        </div>
        {(error || helperText) && (
          <div style={{ ...helperTextStyles, color: helperColor }} role={error ? 'alert' : undefined}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
